import React from 'react';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import cn from 'classnames';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import { SvgProps } from 'apps/sigma2-ui/src/components/svg/types';
import { IconDismissFilterSvg } from 'apps/sigma2-ui/src/filters-v2/components/IconDismissFilter';
import { TranslatedContent } from 'libs/end-to-end/src/translation-content';

export interface SecondaryFilterChipProps {
  /**
   * Callback when the chip is clicked
   */
  onClick: () => void;

  /**
   * Callback when the dismiss button is clicked
   */
  onDismiss: () => void;

  /**
   * Whether the chip can be dismissed
   */
  canDismiss: boolean;

  /**
   * Whether the chip is disabled
   */
  disabled: boolean;

  /**
   * Whether the chip is active (this turns it blue similiar to hover, but only if disabled is false)
   */
  active: boolean;

  /**
   * Whether the chip has a selection, which bolds the right side of the button
   */
  hasSelection: boolean;

  /**
   * Icon for the left side of the button
   */
  icon: React.FC<SvgProps> | null;

  /**
   * Translated content for the left side of the button
   */
  leftTranslatedContent: TranslatedContent;

  /**
   * Translated content for the right side of the button
   */
  rightTranslatedContent: TranslatedContent;
}

export const SecondaryFilterChip = ({
  onClick,
  onDismiss,
  canDismiss,
  disabled,
  active,
  icon,
  hasSelection,
  leftTranslatedContent,
  rightTranslatedContent,
}: SecondaryFilterChipProps) => {
  return (
    <div className={'inline-block max-w-full'}>
      <TouchTarget
        disabled={disabled}
        content={({ hovered }) => {
          return (
            <span
              className={cn(
                'flex rounded-full py-2 px-3 border-[1px] gap-2 items-center transition-all',
                {
                  'opacity-50 cursor-not-allowed': disabled,
                  'opacity-100 cursor-pointer': !disabled,
                  'bg-white border-gray-200': !hovered && !active,
                  'bg-royal-100 border-royal-400': hovered || active,
                },
              )}
            >
              {icon &&
                icon({
                  svg: {
                    className: cn('w-[1em] h-[1em] transition-all', {
                      'text-royal-700': hovered || active,
                      'text-gray-900': !hovered && !active,
                    }),
                  },
                })}

              <S2Typography
                translatedContent={leftTranslatedContent}
                variant={'body-7'}
                textColorVariant={hovered || active ? 'royal-700' : 'gray-900'}
              />
              <span
                className={cn('w-[1px] h-[16px] transition-all', {
                  'bg-gray-200': !hovered && !active,
                  'bg-royal-700': hovered || active,
                })}
              />
              <S2Typography
                translatedContent={rightTranslatedContent}
                variant={hasSelection ? 'headline-7-alt' : 'body-7'}
                textColorVariant={
                  hovered || active
                    ? 'royal-700'
                    : hasSelection
                      ? 'gray-900'
                      : 'gray-600'
                }
                truncate={true}
                flex={true}
              />

              {canDismiss && (
                <span
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onDismiss();
                  }}
                >
                  <IconDismissFilterSvg
                    svg={{
                      className: cn('w-[1em] h-[1em] transition-all', {
                        'hover:text-red-600 cursor-pointer': !disabled,
                      }),
                    }}
                  />
                </span>
              )}
            </span>
          );
        }}
        behavior={{ type: 'onClick', onClick }}
      ></TouchTarget>
    </div>
  );
};
